import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Unlayer Editor Component
 * Integrates Unlayer's visual page builder for creating landing pages and email templates
 * 
 * @param {Object} props
 * @param {Object} props.design - Initial design JSON from Unlayer
 * @param {string} props.htmlContent - Optional HTML content to display as fallback when design JSON is not available
 * @param {Function} props.onSave - Callback when design is saved (receives { html, json })
 * @param {string} props.editorId - Unique ID for the editor instance
 * @param {string} props.displayMode - 'email' or 'web'
 * @param {boolean} props.showSaveButton - Show save button
 * @param {Array} props.mergeTags - Custom merge tags to show in editor (VoiceBox format: %%TAG%%)
 * @param {Array} props.availableFields - Available fields for dynamic merge tags
 * @param {Function} props.onTagsExtracted - Callback when tags are extracted from design
 */
export default function UnlayerEditor({
    design,
    htmlContent,
    onSave,
    editorId = 'unlayer-editor',
    displayMode = 'email',
    showSaveButton = true,
    mergeTags = [],
    availableFields = [],
    onTagsExtracted,
    showDragDropTags = true
}) {
    const editorRef = useRef(null);
    const unlayerRef = useRef(null);
    const onSaveRef = useRef(onSave);
    const onTagsExtractedRef = useRef(onTagsExtracted);
    const designRef = useRef(design);
    const htmlContentRef = useRef(htmlContent);
    const editorReadyRef = useRef(false);
    const internalUpdateRef = useRef(false);
    const initialDesignLoadedRef = useRef(false);
    const [draggedTag, setDraggedTag] = useState(null);
    const [showHtmlFallback, setShowHtmlFallback] = useState(false);
    const [showAllDealershipTags, setShowAllDealershipTags] = useState(false);
    const [showAllCustomerTags, setShowAllCustomerTags] = useState(false);

    // Keep refs in sync with latest props without triggering re-initialization
    useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
    useEffect(() => { onTagsExtractedRef.current = onTagsExtracted; }, [onTagsExtracted]);
    useEffect(() => { designRef.current = design; }, [design]);
    useEffect(() => { htmlContentRef.current = htmlContent; }, [htmlContent]);

    function isValidUnlayerDesign(obj) {
        if (!obj || typeof obj !== 'object') return false;
        if (obj.body && typeof obj.body === 'object') return true;
        if (obj.schemaVersion) return true;
        return false;
    }

    // Only reload design from prop when it's an external change (not from the editor itself)
    useEffect(() => {
        if (!editorReadyRef.current || !unlayerRef.current) return;
        if (!initialDesignLoadedRef.current) return;

        if (internalUpdateRef.current) {
            internalUpdateRef.current = false;
            return;
        }

        if (design && typeof design === 'object' && Object.keys(design).length > 0) {
            if (isValidUnlayerDesign(design)) {
                try {
                    unlayerRef.current.loadDesign(design);
                    setShowHtmlFallback(false);
                } catch (err) {
                    console.error('Error reloading design:', err);
                }
            } else {
                setShowHtmlFallback(true);
            }
        }
    }, [design]);

    // Initialize editor once on mount, tear down on unmount
    useEffect(() => {
        let destroyed = false;

        function initializeEditor() {
            if (destroyed || !window.unlayer) {
                console.error('Unlayer script failed to load or component unmounted');
                return;
            }

            const editorElement = document.getElementById(editorId);
            if (editorElement) editorElement.innerHTML = '';

            if (unlayerRef.current) {
                try {
                    if (typeof unlayerRef.current.removeEventListener === 'function')
                        unlayerRef.current.removeEventListener('design:updated');
                    if (typeof unlayerRef.current.destroy === 'function')
                        unlayerRef.current.destroy();
                } catch (_) { /* ignore */ }
                unlayerRef.current = null;
                editorReadyRef.current = false;
            }

            // Build merge tags in Unlayer's dictionary format:
            // { tagKey: { name: "Display Name", value: "%%Tag%%" } }
            // Grouped: { groupKey: { name: "Group", mergeTags: { ... } } }
            const defaultTags = [
                { name: 'First Name', value: '%%FirstName%%' },
                { name: 'Last Name', value: '%%LastName%%' },
                { name: 'Email', value: '%%Email%%' },
                { name: 'Phone', value: '%%Phone%%' }
            ];

            const fieldTags = (availableFields || []).map(field => ({
                name: field.label || field.name || field,
                value: `%%${typeof field === 'string' ? field : (field.tag || field.name)}%%`
            }));

            const allTags = [...defaultTags, ...(mergeTags || []), ...fieldTags];
            const uniqueTags = Array.from(
                new Map(allTags.map(tag => [tag.value, tag])).values()
            );

            function buildMergeTagsDict(tags) {
                const dict = {};
                tags.forEach(tag => {
                    const key = (tag.value || '').replace(/%%/g, '').replace(/\s+/g, '_') || tag.name?.replace(/\s+/g, '_');
                    if (key) {
                        dict[key] = { name: tag.name, value: tag.value };
                    }
                });
                return dict;
            }

            const customerKeys = ['FirstName', 'LastName', 'Email', 'Phone'];
            const customerTags = uniqueTags.filter(t => customerKeys.some(k => t.value?.includes(k)));
            const dealershipTags = uniqueTags.filter(t => !customerKeys.some(k => t.value?.includes(k)));

            const mergeTagsDict = {};
            if (customerTags.length > 0) {
                mergeTagsDict.customer_info = {
                    name: 'Customer Information',
                    mergeTags: buildMergeTagsDict(customerTags)
                };
            }
            if (dealershipTags.length > 0) {
                mergeTagsDict.dealership_info = {
                    name: 'Dealership Information',
                    mergeTags: buildMergeTagsDict(dealershipTags)
                };
            }

            window.unlayer.init({
                id: editorId,
                displayMode,
                projectId: process.env.REACT_APP_UNLAYER_PROJECT_ID || null,
                tools: {
                    form: { enabled: true },
                    button: { enabled: true },
                    divider: { enabled: true },
                    image: { enabled: true },
                    social: { enabled: true },
                    spacer: { enabled: true },
                    text: { enabled: true },
                    video: { enabled: true },
                    html: { enabled: true },
                    countdown: { enabled: true },
                    survey: { enabled: true }
                },
                appearance: {
                    theme: 'light',
                    panels: { tools: { dock: 'left' } },
                    colors: { primary: '#2563eb', secondary: '#64748b' }
                },
                locale: 'en',
                ...(displayMode === 'email' && {
                    mergeTags: mergeTagsDict,
                    mergeTagsConfig: { sort: false },
                    features: {
                        textEditor: { inlineFontControls: true }
                    }
                }),
                customJS: [],
                customCSS: [],
                user: { id: 'onboarding-user', name: 'Onboarding User' }
            });

            // The embed.js API exposes methods on the global unlayer object
            unlayerRef.current = window.unlayer;

            window.unlayer.addEventListener('editor:ready', () => {
                if (destroyed) return;
                editorReadyRef.current = true;
                console.log('UnLayer editor:ready event fired');

                window.unlayer.addEventListener('design:updated', handleDesignUpdate);

                const currentDesign = designRef.current;
                const currentHtml = htmlContentRef.current;

                const hasDesign = currentDesign && typeof currentDesign === 'object' && Object.keys(currentDesign).length > 0;
                const isNativeUnlayer = hasDesign && isValidUnlayerDesign(currentDesign);

                console.log('[UnlayerEditor] design keys:', hasDesign ? Object.keys(currentDesign) : 'none');
                console.log('[UnlayerEditor] isNativeUnlayer:', isNativeUnlayer);
                console.log('[UnlayerEditor] htmlContent length:', currentHtml ? currentHtml.length : 0);

                if (hasDesign && isNativeUnlayer) {
                    try {
                        window.unlayer.loadDesign(currentDesign);
                        setShowHtmlFallback(false);
                    } catch (err) {
                        console.error('[UnlayerEditor] loadDesign error:', err);
                        if (currentHtml) {
                            setShowHtmlFallback(true);
                        } else {
                            window.unlayer.loadDesign({});
                        }
                    }
                } else if (currentHtml) {
                    setShowHtmlFallback(false);
                    try {
                        window.unlayer.loadDesign({
                            body: {
                                rows: [{
                                    cells: [1],
                                    columns: [{
                                        contents: [{
                                            type: 'html',
                                            values: {
                                                html: currentHtml
                                            }
                                        }]
                                    }]
                                }]
                            }
                        });
                    } catch (err) {
                        console.warn('[UnlayerEditor] Failed to load HTML into editor, showing fallback:', err);
                        setShowHtmlFallback(true);
                    }
                } else {
                    window.unlayer.loadDesign({});
                    setShowHtmlFallback(false);
                }
                initialDesignLoadedRef.current = true;
            });
        }

        function handleDesignUpdate() {
            if (!unlayerRef.current) return;
            internalUpdateRef.current = true;
            unlayerRef.current.exportHtml((data) => {
                const tagRegex = /%%([A-Za-z][A-Za-z0-9_]*)%%/g;
                const tags = new Set();
                const contentToSearch = JSON.stringify(data.design || {}) + (data.html || '');
                let match;
                while ((match = tagRegex.exec(contentToSearch)) !== null) {
                    tags.add(match[1]);
                }
                const extractedTags = Array.from(tags).sort();

                if (onTagsExtractedRef.current && extractedTags.length > 0) {
                    onTagsExtractedRef.current(extractedTags);
                }

                if (onSaveRef.current) {
                    onSaveRef.current({
                        html: data.html,
                        json: data.design,
                        tags: extractedTags
                    });
                }
            });
        }

        if (!window.unlayer) {
            const script = document.createElement('script');
            script.src = 'https://editor.unlayer.com/embed.js';
            script.async = true;
            script.onload = initializeEditor;
            document.body.appendChild(script);
        } else {
            initializeEditor();
        }

        return () => {
            destroyed = true;
            if (unlayerRef.current) {
                try {
                    if (typeof unlayerRef.current.removeEventListener === 'function')
                        unlayerRef.current.removeEventListener('design:updated');
                    if (typeof unlayerRef.current.destroy === 'function')
                        unlayerRef.current.destroy();
                } catch (_) { /* ignore */ }
                unlayerRef.current = null;
                editorReadyRef.current = false;
            }
            const editorElement = document.getElementById(editorId);
            if (editorElement) editorElement.innerHTML = '';
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorId, displayMode]);

    const handleSave = useCallback(() => {
        if (unlayerRef.current) {
            unlayerRef.current.exportHtml((data) => {
                if (onSaveRef.current) {
                    onSaveRef.current({
                        html: data.html,
                        json: data.design
                    });
                }
            });
        }
    }, []);

    const allTags = [
        ...mergeTags,
        ...availableFields.map(field => ({
            name: field.label || field.name || field,
            value: typeof field === 'string' ? `%%${field}%%` : (field.value || `%%${field.tag || field.name}%%`),
            category: field.category || 'customer'
        }))
    ];

    return (
        <div className="unlayer-editor-container" style={{ position: 'relative' }}>
            {showDragDropTags && allTags.length > 0 && (
                <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Drag & Drop Tags into Editor:</p>
                    <div className="grid grid-cols-2 gap-4">
                        {mergeTags.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-green-700 mb-2">🏢 Dealership Tags</p>
                                <div className="flex flex-wrap gap-2">
                                    {(showAllDealershipTags ? mergeTags : mergeTags.slice(0, 10)).map((tag, idx) => (
                                        <span
                                            key={idx}
                                            draggable
                                            onDragStart={(e) => {
                                                setDraggedTag(tag.value);
                                                e.dataTransfer.setData('text/plain', tag.value);
                                                e.dataTransfer.effectAllowed = 'copy';
                                            }}
                                            onDragEnd={() => setDraggedTag(null)}
                                            className={`px-3 py-1.5 bg-green-100 text-green-800 rounded text-xs font-mono cursor-move hover:bg-green-200 border-2 ${
                                                draggedTag === tag.value ? 'border-green-500' : 'border-transparent'
                                            }`}
                                            title={`Drag to insert: ${tag.value}`}
                                        >
                                            {tag.value}
                                        </span>
                                    ))}
                                    {mergeTags.length > 10 && (
                                        <button
                                            onClick={() => setShowAllDealershipTags(!showAllDealershipTags)}
                                            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 border-2 border-transparent hover:border-gray-300 cursor-pointer"
                                            title={showAllDealershipTags ? 'Show fewer tags' : `Show all ${mergeTags.length} tags`}
                                        >
                                            {showAllDealershipTags
                                                ? `Show less (${mergeTags.length - 10} hidden)`
                                                : `+${mergeTags.length - 10} more`
                                            }
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        {availableFields.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-blue-700 mb-2">👤 Customer Wildcards</p>
                                <div className="flex flex-wrap gap-2">
                                    {(showAllCustomerTags ? availableFields : availableFields.slice(0, 10)).map((field, idx) => {
                                        const tagValue = typeof field === 'string' ? `%%${field}%%` : (field.value || `%%${field.tag || field.name}%%`);
                                        return (
                                            <span
                                                key={idx}
                                                draggable
                                                onDragStart={(e) => {
                                                    setDraggedTag(tagValue);
                                                    e.dataTransfer.setData('text/plain', tagValue);
                                                    e.dataTransfer.effectAllowed = 'copy';
                                                }}
                                                onDragEnd={() => setDraggedTag(null)}
                                                className={`px-3 py-1.5 bg-blue-100 text-blue-800 rounded text-xs font-mono cursor-move hover:bg-blue-200 border-2 ${
                                                    draggedTag === tagValue ? 'border-blue-500' : 'border-transparent'
                                                }`}
                                                title={`Drag to insert: ${tagValue}`}
                                            >
                                                {tagValue}
                                            </span>
                                        );
                                    })}
                                    {availableFields.length > 10 && (
                                        <button
                                            onClick={() => setShowAllCustomerTags(!showAllCustomerTags)}
                                            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 border-2 border-transparent hover:border-gray-300 cursor-pointer"
                                            title={showAllCustomerTags ? 'Show fewer tags' : `Show all ${availableFields.length} tags`}
                                        >
                                            {showAllCustomerTags
                                                ? `Show less (${availableFields.length - 10} hidden)`
                                                : `+${availableFields.length - 10} more`
                                            }
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                        💡 Tip: Drag tags into text elements in the UnLayer editor to insert them. UnLayer will automatically handle the merge tag insertion.
                    </p>
                </div>
            )}
            {showHtmlFallback ? (
                <div style={{ position: 'relative' }}>
                    <div className="mb-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-800">
                            <strong>Note:</strong> This template only has HTML content. Full editing requires UnLayer design JSON.
                            The HTML is displayed below for preview only.
                        </p>
                    </div>
                    <iframe
                        title="HTML Content Preview"
                        className="w-full border rounded"
                        style={{ height: '600px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        srcDoc={htmlContent || '<p>No HTML content available</p>'}
                    />
                </div>
            ) : (
                <div id={editorId} style={{ height: '600px', border: '1px solid #e5e7eb', borderRadius: '8px', position: 'relative' }} ref={editorRef}></div>
            )}
            {showSaveButton && !showHtmlFallback && (
                <div className="mt-4 flex justify-end gap-2">
                    <button
                        onClick={() => {
                            if (unlayerRef.current) {
                                unlayerRef.current.exportHtml((data) => {
                                    const blob = new Blob([JSON.stringify(data.design, null, 2)], { type: 'application/json' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `template-${Date.now()}.json`;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    URL.revokeObjectURL(url);
                                });
                            }
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                        title="Export template as JSON"
                    >
                        Export JSON
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Save Design
                    </button>
                </div>
            )}
        </div>
    );
}
